/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { IntegratedSystem } from "../api/integratedsystems";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import { useSystemOrProject } from "../components/integrated-systems/hooks";
import {
  useGetChangeEntityMetrics,
  useGetWrittenChangesStats,
  useScoreStats,
  useSystemDetails,
  useSystemStats,
} from "../hooks/components/dashboard/useDashboardData";
import { useDashboardStatsSplitByEntity } from "../hooks/components/dashboard/useDashboardStats";
import { DashboardHeader } from "../components/dashboard/DashboardHeader";
import { DashboardContactStats } from "../components/dashboard/DashboardContactStats";
import { LoadingDisplay } from "../components/dashboard/LoadingDisplay";
import { DashboardAccountStats } from "../components/dashboard/DashboardAccountStats";
import { Spacer } from "../components/utils/Spacer";
import { DashboardReporting } from "../components/dashboard/DashboardReporting";
import { DataProject } from "../api/dataprojects";
import { PageTitle } from "../components/utils/PageTitle";

interface CirromDashboardProps {
  userSystems: IntegratedSystem[] | DataProject[];
  setSelectedSystemId: (id: number) => void;
  isDataProject?: boolean;
}

export const CirromDashboard = React.memo(
  (props: CirromDashboardProps) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const { integratedSystemId } = useParams();
    useEffect(() => {
      if (integratedSystemId) {
        if (props.isDataProject) {
          // For data projects, find the project and use its integrated_system_id
          const dataProject = (props.userSystems as DataProject[]).find(
            (p) => p.id === Number.parseInt(integratedSystemId)
          );
          if (dataProject) {
            props.setSelectedSystemId(dataProject.integrated_system_id);
          }
        } else {
          props.setSelectedSystemId(Number.parseInt(integratedSystemId));
        }
      }
    }, [integratedSystemId, props.isDataProject, props.userSystems]);

    const systemOrProject = useSystemOrProject(
      props.userSystems,
      integratedSystemId
    );

    const systemStats = useSystemStats(
      props.isDataProject ? null : systemOrProject,
      props.isDataProject && systemOrProject ? systemOrProject.id : null
    );

    const statsByEntity = useDashboardStatsSplitByEntity(
      systemStats.systemStats,
      integratedSystemId,
      props.isDataProject
    );

    const systemDetails = useSystemDetails(
      props.isDataProject ? null : systemOrProject,
      props.isDataProject && systemOrProject ? systemOrProject.id : null
    );

    const scoreStats = useScoreStats(
      props.isDataProject ? null : systemOrProject,
      props.isDataProject && systemOrProject ? systemOrProject.id : null
    );

    const writtenChangesStats = useGetWrittenChangesStats(
      props.isDataProject ? null : systemOrProject,
      props.isDataProject && systemOrProject ? systemOrProject.id : null
    );

    const changeEntityMetrics = useGetChangeEntityMetrics(
      props.isDataProject ? null : systemOrProject,
      props.isDataProject && systemOrProject ? systemOrProject.id : null
    );

    useEffect(() => {
      if (
        !integratedSystemId &&
        props.userSystems &&
        props.userSystems.length > 0
      ) {
        navigate(`/product/dashboard/${props.userSystems[0].id}`);
      }
    }, [integratedSystemId, props.userSystems, navigate]);

    if (systemDetails.isLoading) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom" />
          <LoadingDisplay />
        </React.Fragment>
      );
    }

    if (!systemOrProject) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom" />
        </React.Fragment>
      );
    }

    return (
      <PageTitle
        pageTitle="DQ Dashboard | CIRROM"
        pageHeader={`DQ Data`}
        isDataProject={props.isDataProject}
        integratedSystemId={systemOrProject?.id}
        systemOrProjectHeader={systemOrProject?.name}
        zeroPadding
      >
        <DashboardHeader
          system={systemOrProject}
          currentUser={auth.currentUser}
        />
        <DashboardContactStats
          stats={statsByEntity.contact}
          isLoading={systemStats.isLoading}
        />
        <Spacer />
        <DashboardAccountStats
          stats={statsByEntity.account}
          isLoading={systemStats.isLoading}
        />
        <Spacer />
        <DashboardReporting
          systemDetails={systemDetails.systemDetails}
          scoreStats={scoreStats.scoreStats}
          writtenChanges={writtenChangesStats.writtenChangesStats}
          isLoading={scoreStats.isLoading}
          changeEntityMetrics={changeEntityMetrics.changeEntityMetrics}
          systemType={systemOrProject?.system_type}
        />
      </PageTitle>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.userSystems === nextProps.userSystems &&
      prevProps.isDataProject === nextProps.isDataProject &&
      prevProps.setSelectedSystemId === nextProps.setSelectedSystemId
    );
  }
);
